import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Images, Loader2, Trash2, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 12;

// Provider photo gallery manager — photos show on the public profile page.
export default function GalleryCard() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/gallery`, { headers });
      setPhotos(res.data || []);
    } catch {
      // silent — gallery is optional
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo too large (max 5MB)');
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API_URL}/api/dashboard/gallery`, form, { headers });
      toast.success('Photo added to your gallery');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/dashboard/gallery/${photoId}`, { headers });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success('Photo removed');
    } catch {
      toast.error('Could not remove photo');
    }
  };

  return (
    <Card data-testid="gallery-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Images className="h-5 w-5" />
          Photo Gallery
        </CardTitle>
        <CardDescription>
          Show off your work — these photos appear on your public profile. Up to {MAX_PHOTOS} photos, 5MB each.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="gallery-file-input" />
        <Button
          variant="outline"
          disabled={uploading || photos.length >= MAX_PHOTOS}
          onClick={() => fileRef.current?.click()}
          data-testid="gallery-upload-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {photos.length >= MAX_PHOTOS ? 'Gallery full' : 'Add Photo'}
        </Button>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : photos.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="gallery-empty">No photos yet. Add a few to help your services sell themselves.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="gallery-grid">
            {photos.map((p) => (
              <div key={p.id} className="relative group aspect-square rounded-md border overflow-hidden bg-muted" data-testid={`gallery-photo-${p.id}`}>
                <img
                  src={`${API_URL}/api/providers/${p.providerId}/gallery/${p.id}/image`}
                  alt={p.caption || 'Gallery photo'}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="absolute top-1 right-1 p-1.5 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-delete-${p.id}`}
                  aria-label="Delete photo"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
